import React, { useState, useMemo } from 'react'
import { Card, Button, Badge, EmptyState } from './ui'
import { TELESALES_AGENTS } from '../lib/constants'
import { Phone, Check, AlertTriangle, CalendarClock, UserPlus, CalendarDays, NotebookPen, CheckSquare } from 'lucide-react'
import { dayAgenda } from '../lib/calendar'
import Diary from './Diary'
import Todos from './Todos'
import DayCalendar from './DayCalendar'
import LeadDetail from './LeadDetail'

// Business timezone = Asia/Jakarta (WIB). en-CA locale yields YYYY-MM-DD.
export function todayWIB() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' })
}
function addDays(dateStr, n) {
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return d.toLocaleDateString('en-CA')
}
const norm = (s) => (s || '').toString().trim().toLowerCase()

// A single work-queue row. Clicking the main area opens the lead dossier.
function WorkRow({ accent, title, sub, meta, right, onLead }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
      <div style={{ width: 4, alignSelf: 'stretch', borderRadius: 4, background: accent, flexShrink: 0 }} />
      <button onClick={onLead} title="Lihat detail lead" style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none', cursor: onLead ? 'pointer' : 'default', padding: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: onLead ? '#185FA5' : '#111', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: '#555', marginTop: 1 }}>{sub}</div>}
        {meta && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{meta}</div>}
      </button>
      {right}
    </div>
  )
}

// Clickable category button shown at the top of the workspace.
function CategoryCard({ icon: Icon, label, count, color, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      flex: '1 1 150px', textAlign: 'left', cursor: 'pointer', background: '#fff',
      border: '1px solid ' + (active ? color : 'rgba(0,0,0,0.1)'),
      boxShadow: active ? `0 0 0 3px ${color}22` : 'none',
      borderRadius: 12, padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '18', color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#111', lineHeight: 1 }}>{count}</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 3 }}>{label}</div>
      </div>
    </button>
  )
}

export default function MyWorkToday({
  callLogs = [], pipeline = [], reminders = [], dbContacts = [], tasks = [], activities = [],
  diary = [], todos = [],
  markReminderDone, completeTask, showAgentFilter = true,
  userName = '', isSuperadmin = false,
  addDiary, updateDiary, deleteDiary,
  addTodo, updateTodo, deleteTodo,
}) {
  const [agent, setAgent] = useState('')          // '' = all agents (work-queue filter, superadmin)
  const [view, setView] = useState('overdue')     // overdue | duetoday | newleads | diary | todo | calendar
  const [lead, setLead] = useState(null)          // { company, contact } for the detail popup
  const today = todayWIB()
  const in7 = addDays(today, 7)

  const byAgent = (arr, key = 'agent') => agent ? arr.filter(r => (r[key]) === agent) : arr
  const rem = byAgent(reminders).filter(r => !r.done)
  const openTasks = byAgent(tasks, 'owner_agent').filter(t => t.status === 'open' || t.status === 'in_progress')
  const contacts = byAgent(dbContacts)

  const datePart = (iso) => iso ? new Date(iso).toLocaleDateString('en-CA', { timeZone: 'Asia/Jakarta' }) : ''
  const timePart = (iso) => iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jakarta' }) : ''
  const TASK_LABEL = { retry_call: 'Retry call', callback: 'Callback', find_pic: 'Cari PIC', next_action: 'Next action', meeting: 'Meeting', data_fix: 'Perbaiki data', manual: 'Task' }

  const followups = [
    ...rem.map(r => ({
      kind: 'reminder', id: r.id, agent: r.agent, company: r.company, contact: r.prospect_name,
      title: `${r.prospect_name || '(tanpa nama)'} · ${r.company || ''}`,
      sub: r.notes || r.next_action || 'Follow up',
      badge: null, dueDate: r.follow_up_date, dueTime: r.follow_up_time || '',
      onDone: markReminderDone ? () => markReminderDone(r.id) : null,
    })),
    ...openTasks.map(t => ({
      kind: 'task', id: t.id, agent: t.owner_agent, company: t.company_name, contact: t.contact_name,
      title: `${t.title || TASK_LABEL[t.task_type] || 'Task'}${t.company_name ? ' · ' + t.company_name : ''}`,
      sub: t.description || TASK_LABEL[t.task_type] || t.task_type,
      badge: TASK_LABEL[t.task_type] || t.task_type, dueDate: datePart(t.due_at), dueTime: timePart(t.due_at),
      onDone: completeTask ? () => completeTask(t.id) : null,
    })),
  ]

  const overdue  = followups.filter(f => f.dueDate && f.dueDate < today).sort((a, b) => a.dueDate.localeCompare(b.dueDate))
  const dueToday = followups.filter(f => f.dueDate === today).sort((a, b) => (a.dueTime || '').localeCompare(b.dueTime || ''))
  const upcoming = followups.filter(f => f.dueDate > today && f.dueDate <= in7).sort((a, b) => a.dueDate.localeCompare(b.dueDate))
  const newLeads = contacts.filter(c => (c.status || '') === 'Not Yet Contacted')

  const lastCallByCompany = {}
  for (const c of callLogs) {
    const k = norm(c.company)
    if (!k) continue
    if (!lastCallByCompany[k] || (c.call_date || '') > (lastCallByCompany[k].call_date || '')) lastCallByCompany[k] = c
  }
  const lastSeen = (company) => {
    const c = lastCallByCompany[norm(company)]
    return c ? `Terakhir dihubungi ${c.call_date} · ${c.outcome || '—'}` : 'Belum ada histori call'
  }

  const personalAgent = isSuperadmin ? '' : userName
  const calendarCount = useMemo(
    () => dayAgenda({ date: today, tasks, reminders, todos, agent: personalAgent, includeDone: false }).length,
    [today, tasks, reminders, todos, personalAgent]
  )
  const diaryTodayCount = useMemo(
    () => diary.filter(d => d.entry_date === today && (isSuperadmin || norm(d.agent) === norm(userName))).length,
    [diary, today, isSuperadmin, userName]
  )
  const todoOpenCount = useMemo(
    () => todos.filter(t => t.task_date === today && t.status !== 'completed' && (isSuperadmin || norm(t.agent) === norm(userName))).length,
    [todos, today, isSuperadmin, userName]
  )

  const openLead = (company, contact) => { if (company) setLead({ company, contact }) }

  const CATS = [
    { id: 'overdue',  icon: AlertTriangle, label: 'Overdue',               count: overdue.length,  color: '#E24B4A' },
    { id: 'duetoday', icon: CalendarClock, label: 'Jatuh tempo hari ini',  count: dueToday.length, color: '#185FA5' },
    { id: 'newleads', icon: UserPlus,      label: 'Lead baru',             count: newLeads.length, color: '#0F6E56' },
    { id: 'diary',    icon: NotebookPen,   label: 'Catatan harian',        count: diaryTodayCount, color: '#854F0B' },
    { id: 'todo',     icon: CheckSquare,   label: 'To-do pribadi',         count: todoOpenCount,   color: '#4A4499' },
    { id: 'calendar', icon: CalendarDays,  label: "Today's calendar",      count: calendarCount,   color: '#1D9E75' },
  ]

  const isQueueView = view === 'overdue' || view === 'duetoday' || view === 'newleads'

  return (
    <div>
      {lead && (
        <LeadDetail company={lead.company} contact={lead.contact} dbContacts={dbContacts}
          callLogs={callLogs} activities={activities} onClose={() => setLead(null)} />
      )}

      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: '#111' }}>My Work Today</div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
          Ruang kerja harian — {new Date(today + 'T00:00:00').toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} (WIB)
        </div>
      </div>

      {/* Category buttons */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        {CATS.map(c => (
          <CategoryCard key={c.id} icon={c.icon} label={c.label} count={c.count} color={c.color}
            active={view === c.id} onClick={() => setView(c.id)} />
        ))}
      </div>

      {/* Agent filter (superadmin, queue views only) */}
      {isQueueView && showAgentFilter && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: '0.75rem' }}>
          <span style={{ fontSize: 12, color: '#666' }}>Agent:</span>
          <select value={agent} onChange={e => setAgent(e.target.value)}
            style={{ fontSize: 12, padding: '6px 10px', border: '0.5px solid rgba(0,0,0,0.2)', borderRadius: 8, background: '#fff', fontWeight: 600 }}>
            <option value="">Semua</option>
            {TELESALES_AGENTS.map(a => <option key={a}>{a}</option>)}
          </select>
        </div>
      )}

      {/* Panels — one at a time */}
      {view === 'calendar' && (
        <DayCalendar tasks={tasks} reminders={reminders} todos={todos} currentAgent={userName} isSuperadmin={isSuperadmin} />
      )}
      {view === 'diary' && (
        <Diary diary={diary} currentAgent={userName} isSuperadmin={isSuperadmin} onAdd={addDiary} onUpdate={updateDiary} onDelete={deleteDiary} />
      )}
      {view === 'todo' && (
        <Todos todos={todos} currentAgent={userName} isSuperadmin={isSuperadmin} onAdd={addTodo} onUpdate={updateTodo} onDelete={deleteTodo} />
      )}

      {view === 'overdue' && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>Overdue — follow-up terlewat</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#E24B4A', background: '#E24B4A22', padding: '1px 8px', borderRadius: 99 }}>{overdue.length}</span>
          </div>
          {overdue.length === 0 ? <EmptyState>Tidak ada follow-up yang terlewat. Kerja bagus!</EmptyState> : overdue.map(f => (
            <WorkRow key={f.kind + f.id} accent="#E24B4A" title={f.title} sub={f.sub}
              meta={`${f.agent || '—'} · jatuh tempo ${f.dueDate}${f.dueTime ? ' ' + f.dueTime : ''} · ${lastSeen(f.company)}`}
              onLead={() => openLead(f.company, f.contact)}
              right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {f.badge && <Badge variant="info">{f.badge}</Badge>}
                <Badge variant="danger">{Math.max(1, Math.round((new Date(today) - new Date(f.dueDate)) / 86400000))}h telat</Badge>
                {f.onDone && <Button size="sm" variant="success" onClick={f.onDone}><Check size={12} /> Selesai</Button>}
              </div>} />
          ))}
        </Card>
      )}

      {view === 'duetoday' && (
        <>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>Jatuh tempo hari ini</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#185FA5', background: '#185FA522', padding: '1px 8px', borderRadius: 99 }}>{dueToday.length}</span>
            </div>
            {dueToday.length === 0 ? <EmptyState>Tidak ada follow-up jatuh tempo hari ini.</EmptyState> : dueToday.map(f => (
              <WorkRow key={f.kind + f.id} accent="#185FA5" title={f.title} sub={f.sub}
                meta={`${f.agent || '—'}${f.dueTime ? ' · ' + f.dueTime : ''} · ${lastSeen(f.company)}`}
                onLead={() => openLead(f.company, f.contact)}
                right={<div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {f.badge && <Badge variant="info">{f.badge}</Badge>}
                  {f.onDone && <Button size="sm" variant="success" onClick={f.onDone}><Check size={12} /> Selesai</Button>}
                </div>} />
            ))}
          </Card>
          {upcoming.length > 0 && (
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>Akan datang (7 hari ke depan)</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#854F0B', background: '#854F0B22', padding: '1px 8px', borderRadius: 99 }}>{upcoming.length}</span>
              </div>
              {upcoming.map(f => (
                <WorkRow key={f.kind + f.id} accent="#EF9F27" title={f.title} sub={f.sub}
                  meta={`${f.agent || '—'} · ${f.dueDate}${f.dueTime ? ' ' + f.dueTime : ''}`}
                  onLead={() => openLead(f.company, f.contact)}
                  right={f.badge ? <Badge variant="info">{f.badge}</Badge> : null} />
              ))}
            </Card>
          )}
        </>
      )}

      {view === 'newleads' && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>Lead baru belum dihubungi</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#0F6E56', background: '#0F6E5622', padding: '1px 8px', borderRadius: 99 }}>{newLeads.length}</span>
          </div>
          {newLeads.length === 0 ? <EmptyState>Tidak ada lead baru menunggu kontak pertama.</EmptyState> : newLeads.map((c, i) => {
            const primary = (c.contacts && c.contacts[0]) || {}
            return (
              <WorkRow key={c.id || i} accent="#0F6E56"
                title={c.company}
                sub={primary.name ? `${primary.name}${primary.role ? ' · ' + primary.role : ''}` : (c.industry || '')}
                meta={`${c.agent || '—'}${c.source ? ' · ' + (c.source === 'Event' && c.event_name ? 'Event: ' + c.event_name : c.source) : ''}`}
                onLead={() => openLead(c.company, primary.name)}
                right={primary.phone ? (
                  <button onClick={(e) => { e.stopPropagation(); navigator.clipboard && navigator.clipboard.writeText(primary.phone) }}
                    title="Salin nomor" style={{ background: '#F0FAF5', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', color: '#0F6E56', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                    <Phone size={12} /> {primary.phone}
                  </button>
                ) : null} />
            )
          })}
        </Card>
      )}
    </div>
  )
}
